import { useEffect, useState } from 'react';
import type { Commune } from '@app/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { apiErrorMessage } from '@/lib/api';
import { Field } from '@/features/simulations/Field';
import { useCommuneSearch } from './queries';

function useDebounced(value: string, delayMs: number): string {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return debounced;
}

/** Recherche de commune par nom ou code postal ; la liste proposée vient de `geo.api.gouv.fr`. */
export function CommuneSearch({ onSelect }: { onSelect: (commune: Commune) => void }) {
  const [text, setText] = useState('');
  const query = useDebounced(text.trim(), 400);
  const search = useCommuneSearch(query);

  return (
    <div className="flex flex-col gap-2">
      <Field label="Commune (nom ou code postal)" htmlFor="commune">
        <Input
          id="commune"
          value={text}
          onChange={(event) => setText(event.target.value)}
          placeholder="Saint-Denis"
          autoComplete="off"
          className="max-w-md"
        />
      </Field>
      {search.isFetching && <p className="text-sm text-muted-foreground">Recherche…</p>}
      {search.isError && <p className="text-sm text-destructive">{apiErrorMessage(search.error, 'Recherche impossible')}</p>}
      {search.data?.length === 0 && <p className="text-sm text-muted-foreground">Aucune commune trouvée.</p>}
      {search.data && search.data.length > 0 && (
        <ul className="flex max-w-md flex-col gap-1">
          {search.data.map((commune) => (
            <li key={commune.code}>
              <Button variant="outline" className="h-auto w-full justify-between py-2 text-left" onClick={() => onSelect(commune)}>
                <span>
                  {commune.name} <span className="text-muted-foreground">({commune.department})</span>
                </span>
                <span className="text-xs text-muted-foreground">
                  {commune.population === null ? '' : `${commune.population.toLocaleString('fr-FR')} hab.`}
                </span>
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
